import emailValidate from "services/emailValidate";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { __ } from "services/Translator";

// Any script (accented Latin, Greek, Cyrillic, Arabic, CJK, ...) plus combining
// marks, spaces, hyphen and both apostrophe forms. Digits are excluded by
// construction, so "Fran\u00E7ois", "O'Brien" and "van der Berg" all pass.
const NAME_REGEX = /^[\p{L}\p{M}\s'\u2019-]+$/u;
// City follows the name rules; the full stop is kept for "St. Louis".
const CITY_REGEX = /^[\p{L}\p{M}\s'\u2019.-]+$/u;
const STREET_REGEX = /^[\p{L}\p{M}\p{N}\s.,'\u2019#/-]+$/u;
const HAS_LETTER = /\p{L}/u;

export const sanitizeTextInput = (value, maxLength = 250) =>
  String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/[<>"'`;(){}[\]\\]/g, "")
    .trim()
    .slice(0, maxLength);

// Same stripping as sanitizeTextInput but the apostrophe survives \u2014 removing it
// silently turns "O'Brien" into "OBrien". Output is React-escaped on render and
// sanitized again server-side.
const sanitizeIdentityInput = (value, maxLength) =>
  String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/[<>"`;(){}[\]\\]/g, "")
    .trim()
    .slice(0, maxLength);

export const validatePersonName = (value, fieldLabel) => {
  const cleaned = sanitizeIdentityInput(value, 80);
  if (cleaned.length < 2 || !HAS_LETTER.test(cleaned)) {
    return { valid: false, value: cleaned, error: __(`Please enter your ${fieldLabel}`) };
  }
  if (!NAME_REGEX.test(cleaned)) {
    return {
      valid: false,
      value: cleaned,
      error: __("Letters, spaces, hyphens and apostrophes only"),
    };
  }
  return { valid: true, value: cleaned, error: "" };
};

export const validateEmailField = (value) => {
  const cleaned = sanitizeTextInput(value, 250).toLowerCase();
  if (!cleaned) {
    return { valid: false, value: cleaned, error: __("Please enter your email") };
  }
  if (!emailValidate(cleaned)) {
    return { valid: false, value: cleaned, error: __("Please enter a valid email address") };
  }
  return { valid: true, value: cleaned, error: "" };
};

export const validateCity = (value) => {
  const cleaned = sanitizeIdentityInput(value, 100);
  if (cleaned.length < 2 || !HAS_LETTER.test(cleaned)) {
    return { valid: false, value: cleaned, error: __("Please enter your city") };
  }
  if (!CITY_REGEX.test(cleaned)) {
    return {
      valid: false,
      value: cleaned,
      error: __("Letters only — no digits"),
    };
  }
  return { valid: true, value: cleaned, error: "" };
};

export const validateStreetAddress = (value) => {
  const cleaned = sanitizeIdentityInput(value, 200);
  if (cleaned.length < 5 || !HAS_LETTER.test(cleaned)) {
    return { valid: false, value: cleaned, error: __("Please enter your street and house number") };
  }
  if (!STREET_REGEX.test(cleaned)) {
    return {
      valid: false,
      value: cleaned,
      error: __("Street address contains invalid characters"),
    };
  }
  return { valid: true, value: cleaned, error: "" };
};

// Per-country postal formats. `test` runs against the upper-cased, single-spaced
// value (so lower case is always accepted); `format` renders the country's
// canonical casing/spacing from the compact form.
const compactPostal = (value) => value.replace(/[\s-]/g, "");
const splitPostal = (value, at) => compactPostal(value).slice(0, at) + " " + compactPostal(value).slice(at);

const POSTAL_RULES = {
  US: {
    test: /^\d{5}(-\d{4})?$/,
    example: "12345 or 12345-6789",
  },
  CA: {
    test: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/,
    format: (v) => splitPostal(v, 3),
    example: "A1A 1A1",
  },
  GB: {
    test: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/,
    format: (v) => splitPostal(v, compactPostal(v).length - 3),
    example: "SW1A 1AA",
  },
  IE: {
    // Eircode: routing key + unique identifier, both from the reduced alphabet.
    test: /^[AC-FHKNPRTV-Y]\d[\dW] ?[0-9AC-FHKNPRTV-Y]{4}$/,
    format: (v) => splitPostal(v, 3),
    example: "A65 F4E2",
  },
  NL: {
    test: /^\d{4} ?[A-Z]{2}$/,
    format: (v) => splitPostal(v, 4),
    example: "1234 AB",
  },
  AU: { test: /^\d{4}$/, example: "2000" },
  NZ: { test: /^\d{4}$/, example: "6011" },
};

const cleanPostal = (value) =>
  sanitizeIdentityInput(value, 20).toUpperCase().replace(/\s+/g, " ");

/** Canonical casing/spacing for a postal code, for use on blur. */
export const normalizePostalCode = (value, countryCode) => {
  const cleaned = cleanPostal(value);
  const rule = POSTAL_RULES[String(countryCode || "").toUpperCase()];
  if (!rule || !rule.test.test(cleaned)) return cleaned;
  return rule.format ? rule.format(cleaned) : cleaned;
};

export const validatePostalCode = (value, countryCode) => {
  const cleaned = cleanPostal(value);
  if (!cleaned) {
    return { valid: false, value: cleaned, error: __("Please enter your postal code") };
  }

  const rule = POSTAL_RULES[String(countryCode || "").toUpperCase()];
  if (rule) {
    if (!rule.test.test(cleaned)) {
      return {
        valid: false,
        value: cleaned,
        error: __("Please enter a valid postal code") + " (" + rule.example + ")",
      };
    }
    return { valid: true, value: normalizePostalCode(cleaned, countryCode), error: "" };
  }

  // Unknown country: sanity check only, so an unusual format is never rejected.
  if (!/^[A-Z0-9][A-Z0-9 -]{1,11}$/.test(cleaned)) {
    return { valid: false, value: cleaned, error: __("Please enter a valid postal code") };
  }

  return { valid: true, value: cleaned, error: "" };
};

export const validateDateOfBirth = (dateValue) => {
  if (!dateValue) {
    return { valid: false, error: __("Please enter your date of birth") };
  }

  const birth = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(birth.getTime())) {
    return { valid: false, error: __("Please enter a valid date") };
  }

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  if (age < 18) {
    return { valid: false, error: __("You must be at least 18 years old") };
  }
  if (age > 100) {
    return { valid: false, error: __("Please enter a valid date of birth") };
  }

  return { valid: true, error: "" };
};

export const dateToProfileParts = (dateValue) => {
  const birth = dateValue instanceof Date ? dateValue : new Date(dateValue);
  return {
    bday: birth.getDate(),
    bmonth: birth.getMonth() + 1,
    byear: birth.getFullYear(),
  };
};

export const profilePartsToDate = (profile) => {
  const { bday, bmonth, byear } = profile || {};
  if (!bday || !bmonth || !byear) return null;
  const d = new Date(Number(byear), Number(bmonth) - 1, Number(bday));
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Phone rules shared by the account page and the deposit modal. An empty number
 * only passes when `required` is false (the account page, where the field stays
 * optional); anything entered must be a valid number for its country.
 * `countryCode` is the player's registered country and is only used when the
 * number carries no calling code of its own.
 */
export const validatePhoneNumber = (value, countryCode = "US", { required = false } = {}) => {
  const raw = String(value || "").trim();
  if (!raw) {
    return required
      ? { valid: false, value: "", error: __("Please enter your phone number") }
      : { valid: true, value: "", error: "" };
  }

  const pn =
    parsePhoneNumberFromString(raw) ||
    parsePhoneNumberFromString(raw, String(countryCode || "US").toUpperCase());
  if (!pn || !pn.isValid()) {
    return { valid: false, value: raw, error: __("Please enter a valid phone number") };
  }
  return { valid: true, value: pn.number, error: "" };
};

export const parsePhoneForProfile = (e164, fallbackCountry = "US") => {
  const raw = String(e164 || "").trim();
  if (!raw) {
    return { valid: false, phoneCode: "", phone: "", error: __("Please enter a valid phone number") };
  }

  const pn = parsePhoneNumberFromString(raw);
  if (!pn || !pn.isValid()) {
    const fallback = parsePhoneNumberFromString(raw, fallbackCountry);
    if (!fallback || !fallback.isValid()) {
      return { valid: false, phoneCode: "", phone: "", error: __("Please enter a valid phone number") };
    }
    return {
      valid: true,
      phoneCode: `+${fallback.countryCallingCode}`,
      phone: fallback.nationalNumber,
      error: "",
    };
  }

  return {
    valid: true,
    phoneCode: `+${pn.countryCallingCode}`,
    phone: pn.nationalNumber,
    error: "",
  };
};

/**
 * The E.164 form of an already-stored number, or "" when nothing usable is on
 * file. ⚠️ The back office derives phoneCode from the player's registered
 * country instead of from the number itself, so recombining the pair blindly
 * prefixes a UK number on an AU account with +61 and yields nonsense. Only trust
 * the pair when it parses, then fall back to reading the number on its own.
 */
export const storedPhoneToE164 = (profile) => {
  const digits = String(profile?.phone || "").replace(/\D/g, "");
  if (!digits) return "";

  const code = String(profile?.phoneCode || "").replace(/\D/g, "");
  const country = String(profile?.playerCountry || "").trim().toUpperCase();
  const attempts = [
    code ? parsePhoneNumberFromString(`+${code}${digits}`) : null,
    parsePhoneNumberFromString(`+${digits}`),
    country ? parsePhoneNumberFromString(digits, country) : null,
  ];

  const match = attempts.find((pn) => pn && pn.isValid());
  return match ? match.number : "";
};

// Per-field input filters — strip characters that can never be valid for a field
// as the user types (name never has digits, house number allows "85c"/"85-c" but
// no other letters, etc). Shared by the Account Details tab and the sign-up form.
// The postal filter deliberately does NOT upper-case: lower case is accepted and
// normalizePostalCode renders the canonical form on blur.
export const INPUT_FILTERS = {
  name: (v) => String(v ?? "").replace(/[^\p{L}\p{M}\s'’-]/gu, ""),
  city: (v) => String(v ?? "").replace(/[^\p{L}\p{M}\s'’.-]/gu, ""),
  street: (v) => String(v ?? "").replace(/[^\p{L}\p{M}\p{N}\s.,'’#/-]/gu, ""),
  house: (v) => String(v ?? "").replace(/[^0-9a-zA-Z\s/-]/g, "").slice(0, 10),
  zip: (v) =>
    String(v ?? "")
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .slice(0, 12),
};

// The single definition of the billing profile a player must supply before
// depositing. Email and country are fixed at sign-up and never asked again;
// state/province is conditional on the country having one. Phone is mandatory
// because the payment provider rejects a deposit request without it.
export const PROFILE_REQUIRED_FIELDS = [
  "firstName",
  "lastName",
  "address",
  "city",
  "zip",
  "dateOfBirth",
  "phone",
];

const PROFILE_FIELD_VALIDATORS = {
  firstName: (value) => validatePersonName(value, "first name"),
  lastName: (value) => validatePersonName(value, "last name"),
  address: (value) => validateStreetAddress(value),
  city: (value) => validateCity(value),
  zip: (value, values) => validatePostalCode(value, values.playerCountry),
  dateOfBirth: (value) => validateDateOfBirth(value),
  phone: (value, values) => validatePhoneNumber(value, values.playerCountry, { required: true }),
};

/** Map a /profile payload onto the form shape the validators expect. */
export const profileToFormValues = (profile) => ({
  firstName: profile?.firstName || "",
  lastName: profile?.lastName || "",
  address: profile?.address || "",
  city: profile?.city || "",
  zip: profile?.zip || "",
  state: profile?.state || "",
  playerCountry: String(profile?.playerCountry || "").trim().toUpperCase(),
  dateOfBirth: profilePartsToDate(profile),
  phone: storedPhoneToE164(profile),
});

/**
 * Validate the billing profile the player is entering. `requiresState` comes
 * from the loaded per-country state list — it is the only conditional field.
 */
export const validateProfileForm = (values, { requiresState = false } = {}) => {
  const errors = {};

  PROFILE_REQUIRED_FIELDS.forEach((field) => {
    const result = PROFILE_FIELD_VALIDATORS[field](values[field], values);
    if (!result.valid) errors[field] = result.error;
  });

  if (requiresState && !String(values.state || "").trim()) {
    errors.state = __("Please select your state or province");
  }

  return errors;
};

/** The cleaned/normalised values for the required fields, ready to POST. */
export const normalizeProfileValues = (values) => {
  const out = { state: sanitizeIdentityInput(values.state, 80) };
  PROFILE_REQUIRED_FIELDS.forEach((field) => {
    // Both are split into their own payload keys by the caller (bday/bmonth/byear
    // and phoneCode/phone), so emitting them here would post a bogus column.
    if (field === "dateOfBirth" || field === "phone") return;
    out[field] = PROFILE_FIELD_VALIDATORS[field](values[field], values).value;
  });
  return out;
};

/**
 * Whether a stored profile carries every required field. The deposit gate reads
 * this, so what it accepts is what the payment provider is handed.
 *
 * The identity fields are checked on presence only: /profile/save-basic writes
 * those columns once and refuses to overwrite them, so re-judging a stored value
 * on format would leave a legacy player permanently unable to deposit with no
 * way to correct it. Phone is the exception — it stays editable server-side, so
 * a legacy "12345" CAN be corrected and must be judged on format; passing it
 * through would only move the rejection to the payment provider.
 */
export const isProfileComplete = (profile) => {
  if (!profile) return false;
  const values = profileToFormValues(profile);
  return PROFILE_REQUIRED_FIELDS.every((field) => {
    if (field === "dateOfBirth") return Boolean(values.dateOfBirth);
    if (field === "phone") return PROFILE_FIELD_VALIDATORS.phone(values.phone, values).valid;
    return String(values[field] || "").trim().length > 0;
  });
};

